import { useState, useEffect, useRef } from 'react';
import { activate } from "../../../../utils/keyboard-activate";
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import type { DefaultTagDefinition, CreateTagDefinitionRequest } from '../../../../types/tags';
import { Checkbox } from '../../../../components/Checkbox';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import {
  getMobileQuickTags,
  getOrgTagDefinitions,
  updateMobileQuickTags,
  createTagDefinition,
  updateTagDefinition,
  deleteTagDefinition,
} from '../../../../services/prospects';
import { Button } from '../../../../components/Button';
import { tourAnchor } from '../../../tours/tourAnchors';
import './TagDefinitionManagerModal.css';

interface TagDefinitionManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDefinitionsChanged: (definitions: DefaultTagDefinition[]) => void;
}

const MAX_CATEGORIES = 10;
const MAX_OPTIONS = 10;
const MAX_NAME_LENGTH = 20;

export const TagDefinitionManagerModal = ({
  isOpen,
  onClose,
  onDefinitionsChanged,
}: TagDefinitionManagerModalProps) => {
  const [definitions, setDefinitions] = useState<DefaultTagDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New option inputs per category
  const [newOptionInputs, setNewOptionInputs] = useState<Record<string, string>>({});

  // New category form state
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState<'single-select' | 'multi-select'>('multi-select');
  const [newCategoryFirstOption, setNewCategoryFirstOption] = useState('');
  const [newCategoryError, setNewCategoryError] = useState('');

  // Pending delete confirmation
  const [pendingDeleteName, setPendingDeleteName] = useState<string | null>(null);
  /*
    Mobile quick-tag selection, in pill order. `null` is "never configured" and is what makes mobile
    keep its historical single Interest pill — distinct from [] which means "show none". Held as the
    raw server value so unticking the last box saves [] rather than reverting to the fallback.
  */
  const [quickTags, setQuickTags] = useState<string[] | null>(null);
  const [quickTagMax, setQuickTagMax] = useState(2);
  const [savingQuickTags, setSavingQuickTags] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadDefinitions();
    }
  }, [isOpen]);

  /*
    Escape, the Tab trap and focus restore. This dialog declares `aria-modal="true"`, both for
    screen readers and so the guided tour can see it: the tour's back walk refuses to step out
    of an open dialog by looking for `[role="dialog"][aria-modal="true"]`, and while this modal
    did not declare itself, Back could land the coach on a step spotlighting the page behind it.
    The claim has to be true, so the trap comes with it rather than the attribute alone.

    Escape is off while a delete is pending, as it was before: the confirmation is the thing on
    screen asking the question, and dismissing the whole manager out from under it would answer
    a different one. The confirmation sits inside this dialog, so the Tab trap still holds.
  */
  useDialogFocus({
    isOpen,
    dialogRef,
    onClose,
    handleEscape: !pendingDeleteName,
  });

  const loadDefinitions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [defs, quick] = await Promise.all([
        getOrgTagDefinitions(),
        getMobileQuickTags(),
      ]);
      setQuickTags(quick.tagNames);
      setQuickTagMax(quick.maxCount);
      setDefinitions(defs);
    } catch {
      setError('Failed to load tag definitions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOption = async (tagName: string, optionToRemove: string) => {
    const def = definitions.find((d) => d.tagName === tagName);
    if (!def) return;

    const newOptions = def.options.filter((o) => o !== optionToRemove);

    // Optimistic update
    const updated = definitions.map((d) =>
      d.tagName === tagName ? { ...d, options: newOptions } : d
    );
    setDefinitions(updated);

    try {
      await updateTagDefinition(tagName, newOptions);
      onDefinitionsChanged(updated);
    } catch {
      setDefinitions(definitions); // revert
      setError(`Failed to remove option "${optionToRemove}"`);
    }
  };

  const handleAddOption = async (tagName: string) => {
    const raw = (newOptionInputs[tagName] || '').trim();
    if (!raw) return;

    const def = definitions.find((d) => d.tagName === tagName);
    if (!def) return;

    if (def.options.length >= MAX_OPTIONS) {
      setError(`Maximum ${MAX_OPTIONS} options per category`);
      return;
    }

    if (def.options.some((o) => o.toLowerCase() === raw.toLowerCase())) {
      setError(`"${raw}" already exists in ${tagName}`);
      return;
    }

    const newOptions = [...def.options, raw];

    // Optimistic update
    const updated = definitions.map((d) =>
      d.tagName === tagName ? { ...d, options: newOptions } : d
    );
    setDefinitions(updated);
    setNewOptionInputs((prev) => ({ ...prev, [tagName]: '' }));

    try {
      await updateTagDefinition(tagName, newOptions);
      onDefinitionsChanged(updated);
    } catch {
      setDefinitions(definitions); // revert
      setError(`Failed to add option "${raw}"`);
    }
  };

  /*
    A tick is an ordered selection, not a set: the order the boxes are ticked is the order the pills
    appear on the phone, so a newly ticked category is appended rather than sorted in.
  */
  const handleQuickTagToggle = async (tagName: string, checked: boolean) => {
    const current = quickTags ?? [];
    const next = checked
      ? [...current, tagName]
      : current.filter((name) => name !== tagName);

    if (checked && next.length > quickTagMax) return;

    const previous = quickTags;
    setQuickTags(next);
    setSavingQuickTags(true);
    setError(null);

    try {
      const saved = await updateMobileQuickTags(next);
      setQuickTags(saved.tagNames);
    } catch {
      setQuickTags(previous);
      setError('Could not save the mobile quick tags. Please try again.');
    } finally {
      setSavingQuickTags(false);
    }
  };

  const handleDeleteCategory = async (tagName: string) => {
    setPendingDeleteName(null);

    const updated = definitions.filter((d) => d.tagName !== tagName);
    setDefinitions(updated);

    try {
      await deleteTagDefinition(tagName);
      onDefinitionsChanged(updated);
    } catch {
      setDefinitions(definitions); // revert
      setError(`Failed to delete category "${tagName}"`);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    const firstOption = newCategoryFirstOption.trim();

    if (!name) {
      setNewCategoryError('Category name is required');
      return;
    }

    if (!firstOption) {
      setNewCategoryError('At least one option is required');
      return;
    }

    if (definitions.some((d) => d.tagName.toLowerCase() === name.toLowerCase())) {
      setNewCategoryError(`A category named "${name}" already exists`);
      return;
    }

    if (definitions.length >= MAX_CATEGORIES) {
      setNewCategoryError(`Maximum ${MAX_CATEGORIES} categories allowed`);
      return;
    }

    const request: CreateTagDefinitionRequest = {
      tagName: name,
      tagType: newCategoryType,
      options: [firstOption],
    };

    try {
      const created = await createTagDefinition(request);
      const updated = [...definitions, created];
      setDefinitions(updated);
      onDefinitionsChanged(updated);
      setShowNewCategoryForm(false);
      setNewCategoryName('');
      setNewCategoryType('multi-select');
      setNewCategoryFirstOption('');
      setNewCategoryError('');
    } catch {
      setNewCategoryError('Failed to create category. Please try again.');
    }
  };

  if (!isOpen) return null;

  const sortedDefs = [...definitions].sort((a, b) => a.displayOrder - b.displayOrder);
  const isQuickTag = (tagName: string) => (quickTags ?? []).includes(tagName);
  const quickTagCount = (quickTags ?? []).length;
  const canAddCategory = definitions.length < MAX_CATEGORIES;

  return (
    <div
      className="tag-manager-overlay"
      /*
        Dismiss only on the backdrop itself. This used to be a bare `onClick={onClose}` with a
        `stopPropagation` handler on the dialog to hold clicks back, which is the same behaviour
        — but a click handler on the `role="dialog"` element trips
        no-noninteractive-element-interactions, and the guard belongs on the backdrop that
        actually dismisses rather than on every element inside it.
      */
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="tag-manager-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-manager-modal-title"
      >
        {/* Header */}
        <div className="tag-manager-modal__header">
          <div>
            <h2 id="tag-manager-modal-title" className="tag-manager-modal__title">
              Customize Tags
            </h2>
            <p className="tag-manager-modal__subtitle">
              {definitions.length}/{MAX_CATEGORIES} categories
            </p>
          </div>
          <ModalCloseButton onClick={onClose} {...tourAnchor('players.tags.close')} />
        </div>

        {/* Body */}
        <div className="tag-manager-modal__body" {...tourAnchor('players.tags.customize')}>
          {error && (
            <div className="tag-manager-modal__error" {...activate(() => setError(null))}>
              {error}
            </div>
          )}

          {loading ? (
            <div className="tag-manager-modal__loading">Loading...</div>
          ) : (
            <>
              <p className="tag-manager-modal__mobile-hint">
                {`Pick up to ${quickTagMax} categories to show as quick tags on your own mobile prospect list. This is just for you, and does not change what anyone else sees. `}
                {quickTags === null
                  ? 'Nothing is set yet, so your phone shows Interest.'
                  : quickTagCount === 0
                    ? 'None selected, so your phone shows no quick tags.'
                    : `Your phone shows ${(quickTags ?? []).join(', ')} in that order.`}
              </p>
              {sortedDefs.map((def) => {
                const canAddMore = def.options.length < MAX_OPTIONS;

                return (
                  <div key={def.tagName} className="tag-manager-category">
                    {/* Category header */}
                    <div className="tag-manager-category__header">
                      <div className="tag-manager-category__title-row">
                        <span className="tag-manager-category__name">{def.tagName}</span>
                        <div className="tag-manager-category__badges">
                          {def.isDefault && (
                            <span className="tag-manager-badge tag-manager-badge--default">Default</span>
                          )}
                          {def.isCustom && (
                            <span className="tag-manager-badge tag-manager-badge--custom">Custom</span>
                          )}
                          <span className="tag-manager-badge tag-manager-badge--type">
                            {def.tagType === 'single-select' ? 'Single' : 'Multi'}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="tag-manager-category__delete"
                        onClick={() => setPendingDeleteName(def.tagName)}
                        aria-label={`Delete ${def.tagName} category`}
                        title="Delete category"
                      >
                        Delete
                      </button>
                    </div>

                    <Checkbox
                      className="tag-manager-category__quick-tag"
                      checked={isQuickTag(def.tagName)}
                      disabled={
                        savingQuickTags ||
                        (!isQuickTag(def.tagName) && quickTagCount >= quickTagMax)
                      }
                      onChange={(e) =>
                        handleQuickTagToggle(def.tagName, e.target.checked)
                      }
                      label={
                        <>
                          Quick tag on my phone
                          {isQuickTag(def.tagName) && (
                            <span className="tag-manager-category__quick-tag-order">
                              {` #${(quickTags ?? []).indexOf(def.tagName) + 1}`}
                            </span>
                          )}
                        </>
                      }
                    />

                    {/* Options */}
                    <div className="tag-manager-category__options">
                      {def.options.map((option) => (
                        <span key={option} className="tag-manager-option-chip">
                          {option}
                          {def.options.length > 1 && (
                            <button
                              type="button"
                              className="tag-manager-option-chip__remove"
                              onClick={() => handleRemoveOption(def.tagName, option)}
                              aria-label={`Remove ${option}`}
                            >
                              ×
                            </button>
                          )}
                        </span>
                      ))}

                      {/* Add option input */}
                      {canAddMore ? (
                        <div className="tag-manager-add-option">
                          <input
                            type="text"
                            className="tag-manager-add-option__input"
                            placeholder="Add option..."
                            value={newOptionInputs[def.tagName] || ''}
                            onChange={(e) => {
                              setError(null);
                              setNewOptionInputs((prev) => ({
                                ...prev,
                                [def.tagName]: e.target.value,
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddOption(def.tagName);
                            }}
                            maxLength={MAX_NAME_LENGTH}
                          />
                          <button
                            type="button"
                            className="tag-manager-add-option__btn"
                            onClick={() => handleAddOption(def.tagName)}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <span className="tag-manager-category__max-note">
                          Max {MAX_OPTIONS} options
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* New category form */}
              {showNewCategoryForm ? (
                <div
                  className="tag-manager-new-category"
                  {...tourAnchor('players.tags.category')}
                >
                  <h4 className="tag-manager-new-category__title">New Category</h4>
                  {newCategoryError && (
                    <p className="tag-manager-new-category__error">{newCategoryError}</p>
                  )}
                  <div className="tag-manager-new-category__fields">
                    <input
                      type="text"
                      className="tag-manager-new-category__input"
                      placeholder="Category name (e.g., Arm Strength)"
                      value={newCategoryName}
                      onChange={(e) => {
                        setNewCategoryName(e.target.value);
                        setNewCategoryError('');
                        setError(null);
                      }}
                      maxLength={MAX_NAME_LENGTH}
                      autoFocus
                    />
                    <div className="tag-manager-new-category__type-row">
                      <label className="tag-manager-new-category__type-label">Type:</label>
                      <label className="tag-manager-new-category__radio">
                        <input
                          type="radio"
                          name="newCategoryType"
                          value="multi-select"
                          checked={newCategoryType === 'multi-select'}
                          onChange={() => setNewCategoryType('multi-select')}
                        />
                        Multi-select
                      </label>
                      <label className="tag-manager-new-category__radio">
                        <input
                          type="radio"
                          name="newCategoryType"
                          value="single-select"
                          checked={newCategoryType === 'single-select'}
                          onChange={() => setNewCategoryType('single-select')}
                        />
                        Single-select
                      </label>
                    </div>
                    <input
                      type="text"
                      className="tag-manager-new-category__input"
                      placeholder="First option (required)"
                      value={newCategoryFirstOption}
                      onChange={(e) => {
                        setNewCategoryFirstOption(e.target.value);
                        setNewCategoryError('');
                        setError(null);
                      }}
                      maxLength={MAX_NAME_LENGTH}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateCategory();
                      }}
                    />
                  </div>
                  <div className="tag-manager-new-category__actions">
                    <button
                      type="button"
                      className="tag-manager-new-category__cancel"
                      onClick={() => {
                        setShowNewCategoryForm(false);
                        setNewCategoryName('');
                        setNewCategoryFirstOption('');
                        setNewCategoryError('');
                      }}
                    >
                      Cancel
                    </button>
                    <Button
                      variant="primary"
                      onClick={handleCreateCategory}
                      disabled={!newCategoryName.trim() || !newCategoryFirstOption.trim()}
                    >
                      Create
                    </Button>
                  </div>
                </div>
              ) : canAddCategory ? (
                <button
                  type="button"
                  className="tag-manager-modal__add-category"
                  onClick={() => setShowNewCategoryForm(true)}
                  {...tourAnchor('players.tags.category')}
                >
                  + Add New Category
                </button>
              ) : (
                <p className="tag-manager-modal__max-note">
                  Maximum {MAX_CATEGORIES} categories reached
                </p>
              )}
            </>
          )}
        </div>

        {/* Delete confirmation dialog */}
        {pendingDeleteName && (
          <div className="tag-manager-confirm-overlay">
            <div className="tag-manager-confirm">
              <p className="tag-manager-confirm__message">
                Delete <strong>{pendingDeleteName}</strong>? This will remove the category from all
                tag selectors. Existing prospect values are preserved.
              </p>
              <div className="tag-manager-confirm__actions">
                <button
                  type="button"
                  className="tag-manager-confirm__cancel"
                  onClick={() => setPendingDeleteName(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tag-manager-confirm__delete"
                  onClick={() => handleDeleteCategory(pendingDeleteName)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
